
import React from 'react';
import { MapPin, Clock, Phone, Navigation, ArrowUpCircle } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import FadeIn from './FadeIn';
import SEO from './SEO';

const Contact: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <section className="py-24 bg-[#0a0705] text-fait-cream relative border-t border-fait-brown/20 min-h-screen">
      <SEO 
        title={language === 'he' ? 'צור קשר' : 'Contact'} 
        description={t.contact.addressText + ' - ' + t.contact.quietText}
        path="/contact"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        
        <div className="text-center mb-16">
            <FadeIn direction="down">
              <span className="text-fait-gold font-sans text-sm tracking-widest uppercase mb-2 block">{t.contact.subtitle}</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-fait-gold drop-shadow-md">{t.contact.title}</h2>
            </FadeIn>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            
            {/* Contact Details Card */}
            <FadeIn delay={200} className="h-full">
              <div className="bg-[#1a120b] p-8 md:p-12 rounded-3xl shadow-2xl border border-fait-brown/40 relative overflow-hidden h-full flex flex-col justify-between">
                 
                 {/* Glow Effect */}
                 <div className="absolute top-0 right-0 rtl:right-0 rtl:left-auto ltr:left-0 ltr:right-auto w-full h-1 bg-gradient-to-r from-transparent via-fait-gold to-transparent opacity-50"></div>

                 <div className="space-y-10 relative z-10 text-right rtl:text-right ltr:text-left">
                   
                   {/* Address */}
                   <div className="flex items-start gap-6 group">
                      <div className="bg-fait-brown/30 p-4 rounded-2xl text-fait-gold group-hover:scale-110 transition-transform duration-300 border border-fait-gold/10">
                          <MapPin size={28} />
                      </div>
                      <div>
                          <h3 className="text-xl font-bold font-serif text-white mb-1">{t.contact.addressTitle}</h3>
                          <p className="text-gray-300 font-sans text-lg">{t.contact.addressText}</p>
                          
                          {/* Floor Highlight */}
                          <div className="mt-2 inline-flex items-center gap-2 bg-fait-gold/10 px-3 py-1 rounded-full border border-fait-gold/30">
                              <ArrowUpCircle size={16} className="text-fait-gold" />
                              <span className="text-fait-gold font-bold text-sm">{t.contact.floor}</span>
                          </div>

                          <div className="mt-4">
                              <a 
                                  href="https://waze.com/ul?q=Yehuda+Fait+HaYarkon+39+Bnei+Brak" 
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all duration-300 font-sans font-bold text-sm border border-fait-gold/50 text-fait-gold hover:bg-fait-gold hover:text-fait-dark hover:border-fait-gold shadow-[0_0_15px_rgba(206,172,100,0.1)] hover:shadow-[0_0_20px_rgba(206,172,100,0.4)] group-waze"
                              >
                                  <Navigation size={16} className="group-hover-waze:animate-bounce" />
                                  {t.contact.waze}
                              </a>
                          </div>
                      </div>
                   </div>

                   {/* Hours */}
                   <div className="flex items-start gap-6 group">
                      <div className="bg-fait-brown/30 p-4 rounded-2xl text-fait-gold group-hover:scale-110 transition-transform duration-300 border border-fait-gold/10">
                          <Clock size={28} />
                      </div>
                      <div>
                          <h3 className="text-xl font-bold font-serif text-white mb-1">{t.contact.hoursTitle}</h3>
                          <div className="text-gray-300 font-sans text-lg space-y-1">
                              <p className="text-fait-gold font-bold text-xl">{t.contact.openDay}</p>
                              <p>{t.contact.openHours}</p>
                          </div>
                      </div>
                   </div>

                   {/* Phone */}
                   <div className="flex items-start gap-6 group">
                      <div className="bg-fait-brown/30 p-4 rounded-2xl text-fait-gold group-hover:scale-110 transition-transform duration-300 border border-fait-gold/10">
                          <Phone size={28} />
                      </div>
                      <div>
                          <h3 className="text-xl font-bold font-serif text-white mb-1">{t.contact.phoneTitle}</h3>
                          <a href="tel:0548478033" className="text-gray-300 hover:text-white font-sans text-2xl block transition-colors font-bold tracking-wide">
                              054-847-8033
                          </a>
                      </div>
                   </div>

                 </div>

                 {/* Call to Action Box inside card */}
                 <div className="mt-12 bg-fait-brown/20 rounded-xl p-6 text-center border border-dashed border-fait-gold/30">
                      <div className="text-3xl mb-3">🤫</div>
                      <h4 className="text-xl font-serif font-bold text-fait-cream mb-2">{t.contact.quietCorner}</h4>
                      <p className="text-gray-400 text-sm font-sans mb-4">
                          {t.contact.quietText}
                      </p>
                      <a href="tel:0548478033" className="inline-block bg-fait-gold text-fait-dark font-bold py-2 px-6 rounded-lg text-sm hover:bg-white transition-colors shadow-lg shadow-amber-900/20">
                          {t.contact.callBtn}
                      </a>
                 </div>

              </div>
            </FadeIn>
            
            {/* Map Embed - Updated Source */}
            <FadeIn delay={400} className="h-full min-h-[400px] lg:min-h-0">
                 <div className="rounded-3xl overflow-hidden shadow-2xl border border-fait-gold/20 h-full relative">
                    <iframe 
                        src="https://maps.google.com/maps?q=%D7%94%D7%99%D7%A8%D7%A7%D7%95%D7%9F+39%2C+%D7%91%D7%A0%D7%99+%D7%91%D7%A8%D7%A7&t=&z=17&ie=UTF8&iwloc=&output=embed"
                        width="100%" 
                        height="100%" 
                        style={{border:0, minHeight: '500px'}} 
                        allowFullScreen={true} 
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Yehuda Fait Location"
                        className="grayscale-[50%] invert-[90%] contrast-[1.2] hover:grayscale-0 hover:invert-0 transition-all duration-700"
                    ></iframe>
                 </div>
            </FadeIn>

        </div>
      </div>
    </section>
  );
};

export default Contact;
